import { Routes } from '@angular/router';
import { RenderMode, ServerRoute } from '@angular/ssr';
import { AuthGuard, PrivilegeGuard } from './core/guards/auth.guard';
import { ModuleGuard } from './core/guards/module.guard';
import { PRIVILEGE_KEYS } from './core/services/authorization/privilege-keys';
import { ModuleType } from './core/interfaces/module.interface';
import { PUBLIC_ILLUMINATION_SERVER_ROUTES } from './pages/modules/public-illumination/public-illumination.routes';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/general/login/login.component').then((m) => m.LoginComponent),
    data: { breadcrumb: 'Login' },
  },
  {
    path: 'conheca-o-sipub',
    loadComponent: () => import('./pages/general/service-overview/service-overview.component').then((m) => m.ServiceOverviewComponent),
    data: { breadcrumb: 'Conheça o SipuB' },
  },
  {
    path: 'recover-password/:token',
    loadComponent: () => import('./pages/general/recover-password/recover-password.component').then((m) => m.RecoverPasswordComponent),
    data: { breadcrumb: 'Resetar a Senha' },
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/general/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
    data: { breadcrumb: 'Esqueceu a Senha' },
  },
  {
    path: 'page-not-found',
    loadComponent: () => import('./pages/general/page-not-found/page-not-found.component').then((m) => m.PageNotFoundComponent),
    data: { breadcrumb: 'Página não encontrada' },
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./pages/general/privacy-policy/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
    data: { breadcrumb: 'Políticas de Privacidade' },
  },
  {
    path: 'register-user',
    loadComponent: () => import('./pages/general/register-user/register-user.component').then((m) => m.RegisterUserComponent),
    data: { breadcrumb: 'Cadastro de Usuário' },
  },
  {
    path: 'dashboard',
    canActivate: [AuthGuard],
    loadComponent: () => import('./pages/layout/layout.component').then((m) => m.LayoutComponent),
    data: { breadcrumb: 'Dashboard' },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/general/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: '',
    loadComponent: () => import('./pages/layout/layout.component').then((m) => m.LayoutComponent),
    data: { breadcrumb: 'Home' },
    children: [
      {
        path: 'admin',
        canActivate: [AuthGuard, PrivilegeGuard],
        data: { requiredPrivilege: PRIVILEGE_KEYS.administration },
        loadChildren: () => import('./pages/general/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
      },
      {
        path: 'user-profile',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/general/user-profile/user-profile.component').then((m) => m.UserProfileComponent),
        data: { breadcrumb: 'Perfil do Usuário' },
      },
      {
        path: 'access-logs',
        canActivate: [AuthGuard],
        data: { breadcrumb: 'Histórico de acessos', requiredPrivilege: PRIVILEGE_KEYS.accessLogs },
        loadComponent: () => import('./pages/general/access-logs/access-logs.component').then((m) => m.AccessLogsComponent),
      },
      {
        path: 'manager',
        canActivate: [AuthGuard],
        data: { breadcrumb: 'Gestor' },
        loadChildren: () => import('./pages/general/manager/manager.routes').then((m) => m.MANAGER_ROUTES),
      },
      {
        path: 'occurrences',
        canActivate: [AuthGuard],
        data: { breadcrumb: 'Ocorrências' },
        loadChildren: () => import('./pages/modules/occurrences/occurrences.routes').then((m) => m.OCCURRENCES_ROUTES),
      },
      {
        path: 'public-illumination',
        canActivate: [AuthGuard, ModuleGuard],
        canActivateChild: [PrivilegeGuard],
        data: { breadcrumb: 'Iluminação Pública', moduleId: ModuleType.PublicIllumination.id },
        loadChildren: () => import('./pages/modules/public-illumination/public-illumination.routes').then((m) => m.PUBLIC_ILLUMINATION_ROUTES),
      },
      {
        path: 'consumer-unit',
        canActivate: [AuthGuard, ModuleGuard],
        canActivateChild: [PrivilegeGuard],
        data: { breadcrumb: 'Unidade Consumidora', moduleId: ModuleType.ConsumerUnit.id },
        loadChildren: () => import('./pages/modules/consumer-unit/consumer-unit.routes').then((m) => m.CONSUMER_UNIT_ROUTES),
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: '**', redirectTo: 'page-not-found' },
];

export const serverRoutes: ServerRoute[] = [
  { path: 'recover-password/:token', renderMode: RenderMode.Server },
  { path: 'consumer-unit/edit/:accountId/:id', renderMode: RenderMode.Server },
  { path: 'occurrences/edit-type-occurrence/:id', renderMode: RenderMode.Server },
  { path: 'occurrences/edit-occurrence/:id', renderMode: RenderMode.Server },
  { path: 'occurrences/edit-team/:id', renderMode: RenderMode.Server },
  { path: 'admin/account/edit/:id', renderMode: RenderMode.Server },
  { path: 'admin/users/add', renderMode: RenderMode.Server },
  { path: 'admin/users/edit/:id', renderMode: RenderMode.Server },
  { path: 'admin/people/natural-person/edit/:id', renderMode: RenderMode.Server },
  { path: 'admin/people/legal-entity/edit/:id', renderMode: RenderMode.Server },
  { path: 'admin/roles/edit/:accountId/:id', renderMode: RenderMode.Server },
  ...PUBLIC_ILLUMINATION_SERVER_ROUTES,
  { path: '**', renderMode: RenderMode.Prerender },
];
